import { useEffect } from 'react';
import { useLang } from '@/hooks/useLang';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://rwa99.cn';

const LANG_META: Record<string, { locale: string; name: string; desc: string }> = {
  zh: {
    locale: 'zh_CN',
    name: 'RWA99.cn | 2026 RWA元年 · 合规知识门户 · 真实世界资产代币化',
    desc: '国内首个全覆盖RWA合规知识门户，含政策原文解读、87个全球案例、香港12家持牌VATP图谱、企业落地SOP、26行业上链指南及50+名词百科。',
  },
  en: {
    locale: 'en_US',
    name: "RWA99.cn | 2026 RWA Era · Compliance Portal · Real World Asset Tokenization",
    desc: "China's leading RWA compliance knowledge portal — policy analysis, 87 global cases, Hong Kong licensed VATP directory, enterprise SOP, 26 industry guides and 50+ glossary terms.",
  },
  fr: {
    locale: 'fr_FR',
    name: 'RWA99.cn | 2026 Ère RWA · Portail Conformité · Tokenisation des Actifs Réels',
    desc: "Le portail de référence sur la tokenisation des actifs réels (RWA) — analyse des politiques, 87 cas mondiaux, répertoire VATP agréé à Hong Kong, SOP d'entreprise et guides sectoriels.",
  },
  es: {
    locale: 'es_ES',
    name: 'RWA99.cn | 2026 Era RWA · Portal de Cumplimiento · Tokenización de Activos Reales',
    desc: 'El portal de referencia sobre tokenización de activos del mundo real (RWA) — análisis de políticas, 87 casos globales, directorio VATP con licencia de Hong Kong y guías sectoriales.',
  },
  pt: {
    locale: 'pt_BR',
    name: 'RWA99.cn | 2026 Era RWA · Portal de Conformidade · Tokenização de Ativos Reais',
    desc: 'O portal de referência sobre tokenização de ativos do mundo real (RWA) — análise de políticas, 87 casos globais, diretório VATP licenciado em Hong Kong e guias setoriais.',
  },
  ja: {
    locale: 'ja_JP',
    name: 'RWA99.cn | 2026 RWA元年 · コンプライアンスポータル · リアルワールドアセット',
    desc: 'リアルワールドアセット（RWA）トークン化の権威ある知識ポータル — 政策分析、87の世界的事例、香港認可VATPs、企業SOP、26業界ガイド。',
  },
  de: {
    locale: 'de_DE',
    name: 'RWA99.cn | 2026 RWA-Ära · Compliance-Portal · Tokenisierung Realer Vermögenswerte',
    desc: 'Das maßgebliche Wissensportal zur Tokenisierung realer Vermögenswerte (RWA) — Politikanalyse, 87 globale Fälle, lizenziertes VATP-Verzeichnis in Hongkong und Branchenführer.',
  },
  ko: {
    locale: 'ko_KR',
    name: 'RWA99.cn | 2026 RWA 원년 · 컴플라이언스 포털 · 실물 자산 토큰화',
    desc: '실물 자산 토큰화(RWA)의 권위 있는 지식 포털 — 정책 분석, 87개 글로벌 사례, 홍콩 인가 VATP 디렉토리, 기업 SOP 및 26개 산업 가이드.',
  },
  ar: {
    locale: 'ar_SA',
    name: 'RWA99.cn | 2026 عصر RWA · بوابة الامتثال · ترميز الأصول الحقيقية',
    desc: 'البوابة المرجعية لترميز الأصول الحقيقية (RWA) — تحليل السياسات، 87 حالة عالمية، دليل VATP المرخص في هونغ كونغ وأدلة القطاعات.',
  },
  ru: {
    locale: 'ru_RU',
    name: 'RWA99.cn | 2026 Эра RWA · Портал Соответствия · Токенизация Реальных Активов',
    desc: 'Авторитетный портал знаний о токенизации реальных активов (RWA) — анализ политик, 87 глобальных случаев, каталог лицензированных VATP в Гонконге и отраслевые руководства.',
  },
};

export default function SEOHead() {
  const { lang } = useLang();
  const meta = LANG_META[lang] ?? LANG_META['zh'];

  useEffect(() => {
    // Update document title
    document.title = meta.name;

    // Update meta description
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', meta.desc);

    // Update OG locale
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) ogLocale.setAttribute('content', meta.locale);

    // Update OG title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.name);

    // Update OG description
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.desc);

    // Update Twitter title
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', meta.name);

    // Update Twitter description
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', meta.desc);

    // Update html lang attribute
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;
  }, [lang, meta]);

  useEffect(() => {
    // Remove any existing JSON-LD injected by this component
    const existing = document.querySelectorAll('script[data-seo-jsonld]');
    existing.forEach((el) => el.remove());

    const schemas = [
      // WebSite schema
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'RWA99.cn',
        alternateName: ['RWA99', 'RWA99 Portal', '真实世界资产代币化门户'],
        url: SITE_URL,
        description: meta.desc,
        inLanguage: lang === 'zh' ? 'zh-CN' : lang,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      // Organization schema
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'RWA99.cn',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: 'https://storage.readdy-site.link/project_files/314791f2-664e-475b-9351-85943b20c38c/736acc2c-7adc-4fbe-8b40-f4d6d3d31110_RWA99LOGO.jpg',
          caption: 'RWA99.cn Logo',
          width: 200,
          height: 200,
        },
        description: '聚焦2026 RWA元年，提供真实世界资产代币化领域的合规知识、政策解读、全球案例与企业落地指引。',
        foundingDate: '2026',
        areaServed: ['CN', 'HK', 'SG', 'US', 'GB'],
        knowsAbout: [
          '真实世界资产代币化',
          'Real World Asset Tokenization',
          'RWA合规路径',
          '香港VATP持牌机构',
          '区块链金融监管',
          '企业上链SOP',
          'Hong Kong SFC Compliance',
          'Digital Asset Regulation',
        ],
        sameAs: [],
      },
      // WebPage schema (homepage) - multilingual
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: meta.name,
        description: meta.desc,
        inLanguage: lang === 'zh' ? 'zh-CN' : lang,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        publisher: { '@id': `${SITE_URL}/#organization` },
        datePublished: '2026-01-01',
        dateModified: new Date().toISOString().split('T')[0],
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: lang === 'zh' ? '首页' : 'Home',
              item: SITE_URL,
            },
          ],
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', '.hero-subtitle'],
        },
      },
      // FAQPage schema
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '什么是RWA（真实世界资产代币化）？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'RWA（Real World Assets）是指将现实世界中的资产（如房地产、债券、股权、大宗商品等）通过区块链技术映射为数字代币，实现合规发行、流通与结算的金融创新模式。',
            },
          },
          {
            '@type': 'Question',
            name: '中国内地可以参与RWA吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '根据现行法规，境内严禁虚拟货币交易、炒作与挖矿，境内企业发行RWA须经证监会备案，并通过香港持牌VATP（虚拟资产交易平台）向合格投资者（个人净资产≥800万港元）发行。',
            },
          },
          {
            '@type': 'Question',
            name: '什么是香港VATP（虚拟资产交易平台）？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'VATP（Virtual Asset Trading Platform）是香港证券及期货事务监察委员会（SFC）根据《2022年打击洗钱及恐怖分子资金筹集条例》修订版颁发的持牌虚拟资产交易平台，目前合规获牌机构包括HashKey Exchange、OSL Exchange等共12家。',
            },
          },
          {
            '@type': 'Question',
            name: 'RWA合规发行的唯一路径是什么？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '合规路径为：境内资产确权 → 向中国证监会备案 → 在香港成立SPV → 委托香港持牌VATP发行代币 → 仅向合格投资者募集 → 资金通过CIPS/QFLP合规回流境内。',
            },
          },
          {
            '@type': 'Question',
            name: 'What is RWA (Real World Asset Tokenization)?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'RWA (Real World Assets) refers to the process of mapping real-world assets (such as real estate, bonds, equity, commodities) onto blockchain as digital tokens, enabling compliant issuance, circulation, and settlement.',
            },
          },
          {
            '@type': 'Question',
            name: 'RWA99.cn是什么网站？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'RWA99.cn是国内首个全覆盖RWA合规知识门户，提供RWA政策解读、全球案例深度库、香港持牌机构图谱、企业落地SOP和全行业上链指南。本站仅供科普，不募资、不推介、不交易、不提供投资建议。',
            },
          },
        ],
      },
      // EducationalOrganization / KnowledgeBase schema
      {
        '@context': 'https://schema.org',
        '@type': 'EducationalOrganization',
        '@id': `${SITE_URL}/#edu`,
        name: 'RWA99.cn Knowledge Portal',
        url: SITE_URL,
        description: 'Comprehensive RWA compliance education platform covering tokenization, regulation, and enterprise implementation.',
        teaches: [
          'Real World Asset Tokenization',
          'Blockchain Compliance',
          'Hong Kong Virtual Asset Regulation',
          'Enterprise RWA Implementation SOP',
        ],
        educationalLevel: 'Professional',
        inLanguage: ['zh-CN', 'en', 'ja', 'ko', 'ar', 'fr', 'de', 'es', 'pt', 'ru'],
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
    };
  }, [lang, meta]);

  return null;
}
